import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { uniq } from 'lodash';
import { WORK_CALENDAR_VIEW_ALL_OWN_BUSINESS_RECORDS } from 'scenes/Permissions/constants/permissions';
import { selectIsRequiredBusinessPermissionFromCurrentActiveEmployerBusiness } from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { getAdminWorkCalendarDataFromExternalProject } from 'scenes/WorkDemo/services/getAdminWorkCalendarDataFromExternalProject.service';
import returnProjectIdAndItsChildProjectIds from 'scenes/WorkDemo/utils/returnProjectIdAndItsChildProjectIds';
import { loadAssignmentsForProjects } from 'store/assignments/thunks/loadAssignmentsForProjects';
import {
  selectProjects,
  selectSelectedProjectId,
} from 'store/projects/selectors/projectsSelectors';
import { loadUsersAndPersonalResources } from 'store/projectUsers/thunks/loadUsersAndPersonalResources';
import { loadRecordAllowancesOfEmployerInTimeframe } from 'store/recordAllowances/thunks/loadRecordAllowancesOfEmployerInTimeframe.thunk';
import { loadRecordAllowancesOfProjectInTimeframe } from 'store/recordAllowances/thunks/loadRecordAllowancesOfProjectInTimeframe.thunk';
import { selectUserActiveEmployerInfo } from 'store/user/selectors.js/user.selectors';
import { createSignedGetRequest } from 'utils/createSignedRequestsHelpers';

export const loadInitialCalendarData = createAsyncThunk(
  'workCalendar/loadInitialCalendarData',
  async (_, { getState, dispatch }) => {
    const { workSearchParams_demo } = getState();

    const state = getState();

    const selectedProjectId = selectSelectedProjectId(state);

    const projects = selectProjects(state);

    const projectsArray = Object.values(projects);

    const isPermissionFromActiveEmployerToViewAllBusinessRecords =
      selectIsRequiredBusinessPermissionFromCurrentActiveEmployerBusiness(
        getState(),
        WORK_CALENDAR_VIEW_ALL_OWN_BUSINESS_RECORDS
      );

    const activeEmployerInfo = selectUserActiveEmployerInfo(getState());

    let adminSearchData = {
      resources: [],
      records: [],
      filePermissions: [],
      employers: [],
    };

    const { fromDate, toDate } = workSearchParams_demo;

    if (selectedProjectId) {
      dispatch(loadRecordAllowancesOfProjectInTimeframe({ fromDate, toDate }));
      if (isPermissionFromActiveEmployerToViewAllBusinessRecords) {
        try {
          adminSearchData = await getAdminWorkCalendarDataFromExternalProject(
            selectedProjectId,
            fromDate,
            toDate,
            activeEmployerInfo
          );
        } catch (error) {
          console.log('Error occured with admin search');
        }
      }

      const selectedProject = projects[selectedProjectId];

      let projectsToSearch = [];

      if (selectedProject.businessTypeEnum === 'EMPLOYER') {
        dispatch(
          loadRecordAllowancesOfEmployerInTimeframe({ fromDate, toDate })
        );
        const projectsOfBusiness = projectsArray.filter(
          (project) => project.business_id === selectedProject.business_id
        );

        const projectIds = projectsOfBusiness.map((project) => project.id);

        projectsToSearch = projectIds;
      } else {
        projectsToSearch = returnProjectIdAndItsChildProjectIds(
          [selectedProjectId],
          projectsArray
        );
      }

      const requests = await Promise.all(
        projectsToSearch.map((projectId) => {
          const apiPath = `workflow/init/normal-request/${projectId}/${fromDate}/${toDate}`;

          return createSignedGetRequest({ apiPath });
        })
      );

      const projectAndChildProjects = projectsArray.filter(
        (project) =>
          project && project.id && projectsToSearch.includes(project.id)
      );

      const data = await Promise.all(
        requests.map((request) => axios(request).then((res) => res.data))
      );

      const resources = data
        .map((projectData) => projectData.resources)
        .flat()
        .filter((resource) => resource && resource.id);

      const resourcesIncludingAdminSearchResources = [
        ...resources,
        ...adminSearchData.resources,
      ];

      if (
        resourcesIncludingAdminSearchResources &&
        resourcesIncludingAdminSearchResources.length > 0
      ) {
        dispatch(
          loadUsersAndPersonalResources(resourcesIncludingAdminSearchResources)
        );
      }

      const filePermissions = data
        .map((projectData) => projectData.filePermissions)
        .flat()
        .filter((filePermission) => filePermission && filePermission.id);

      const assignments = data
        .map((projectData) => projectData.assignments)
        .flat()
        .filter((a) => a && a.id);

      const employers = data
        .map((projectData) => projectData.employers)
        .flat()
        .filter((employer) => employer && employer.id);

      const records = data
        .map((projectData) => projectData.records)
        .flat()
        .filter((record) => record && record.id);

      const recordsIncludingAdminSearchRecords = [
        ...records,
        ...adminSearchData.records,
      ];

      const employersIncludingAdminSearchEmployers = [
        ...employers,
        ...adminSearchData.employers,
      ];

      const recordsProjectIds = uniq(
        recordsIncludingAdminSearchRecords
          .filter((record) => record.assignment)
          .map((record) => record.project)
      );

      dispatch(loadAssignmentsForProjects(recordsProjectIds));

      return {
        projectId: selectedProjectId,
        resources: resourcesIncludingAdminSearchResources,
        assignments: assignments,
        employers: employersIncludingAdminSearchEmployers,
        filePermissions: filePermissions,
        projects: projectAndChildProjects,
        records: recordsIncludingAdminSearchRecords,
      };
    } else {
      return;
    }
  }
);
